
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerClose,
} from "@/components/ui/drawer";
import { Guard } from "../types";
import { GuardExpandedInfo } from "./GuardExpandedInfo";
import React from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";

interface ResponsiveGuardDetailsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guard: Guard | null;
  onGuardRemoved?: () => void;
  title?: string;
  description?: string;
}

export const ResponsiveGuardDetails: React.FC<ResponsiveGuardDetailsProps> = ({
  open,
  onOpenChange,
  guard,
  onGuardRemoved,
  title = "Personnel Details",
  description,
}) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const isGuardsRoute = location.pathname.startsWith("/guards");

  // Safety check - if guard is null, don't render anything
  if (!guard) return null;

  // Handle guard removal by closing modal first, then calling parent callback
  const handleGuardRemoved = () => {
    onOpenChange(false); // Close the modal/drawer first
    setTimeout(() => {
      onGuardRemoved?.(); // Then trigger refetch after modal is closed
    }, 100);
  };

  // Display format: Last Name, First Name
  const guardDisplayName = `${guard.last_name} ${guard.first_name} - ${guard.code}`;

  // Drawer mode for mobile or /guards route - slide from right
  if (isMobile || isGuardsRoute) {
    return (
      <Drawer 
        open={open} 
        onOpenChange={onOpenChange}
        direction="right"
      >
        <DrawerContent 
          className="fixed inset-y-0 right-0 h-full w-[90%] sm:w-[900px] rounded-l-xl border-l shadow-xl !max-h-screen !h-screen !overflow-auto flex flex-col animate-slideRight"
          style={{ transform: open ? 'translateX(0)' : 'translateX(100%)' }}
        >
          <DrawerHeader className="sticky top-0 bg-background z-10 border-b pb-4">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>
              {description || guardDisplayName}
            </DrawerDescription>
          </DrawerHeader>
          <DrawerClose asChild>
            <button
              type="button"
              aria-label="Close"
              className="absolute right-4 top-4 z-20 rounded-md bg-transparent hover:bg-muted/60 p-2 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </DrawerClose>
          <div className="px-4 pb-8 pt-4 flex-grow overflow-auto">
            <GuardExpandedInfo guard={guard} onGuardRemoved={handleGuardRemoved} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }
  
  // Dialog mode (desktop/outside /guards route)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="fixed right-0 top-0 !w-[1100px] !max-w-[90vw] !h-screen !max-h-screen !overflow-auto p-0 m-0 rounded-l-xl border-l shadow-xl animate-slideRight"
        style={{
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          inset: '0 0 auto auto', // Top right positioning
        }}
      >
        <DialogHeader className="sticky top-0 bg-background z-10 p-6 border-b">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description || guardDisplayName}
          </DialogDescription>
        </DialogHeader>
        <DialogClose asChild>
          <button
            type="button"
            aria-label="Close"
            className="absolute right-6 top-6 z-20 rounded-md bg-transparent hover:bg-muted/60 p-2 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </DialogClose>
        <div className="flex-grow overflow-auto px-6 pb-6 pt-4">
          <GuardExpandedInfo guard={guard} onGuardRemoved={handleGuardRemoved} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
